import { useMemo, useState } from "react";
import { useApp } from "../../context/AppContext";
import type { MainTab } from "../../models/types";
import { hasSeenTabIntro, markTabIntroSeen } from "../../storage/session";
import { useViewport } from "../../hooks/useViewport";
import { HomeView } from "../home/HomeView";
import {
  FamilyDirectoryView,
  FamilyMapView,
  FamilyTreeView,
} from "../family/FamilyViews";
import { PlayView } from "../play/PlayView";
import { NewsView } from "../news/NewsView";
import { HeritageIntro } from "./HeritageIntro";
import { UsageTracker } from "./UsageTracker";
import { PersonDetailSheet } from "../family/PersonDetail";
import {
  FeedbackReplyModal,
  FeedbackSheet,
  InvitesSheet,
  SettingsSheet,
} from "../settings/SettingsSheets";
import {
  EventComposerSheet,
  AnnouncementComposerSheet,
} from "../news/ComposerSheets";
import { BirthdayPrimingModal } from "../settings/BirthdayPrimingModal";
import { ShellActionButton } from "../shared/ShellActionButton";
import { TabIntroModal } from "../shared/TabIntroModal";
import { LoadingState, ErrorState } from "../shared/LoadingState";
import { ModalSheet } from "../shared/ModalSheet";
import {
  IconGame,
  IconGear,
  IconGrid,
  IconHome,
  IconMap,
  IconNews,
  IconPeople,
  IconPlus,
  IconShare,
  IconTree,
  IconTrophy,
} from "../shared/Icons";

const TABS: { id: MainTab; label: string; Icon: typeof IconHome }[] = [
  { id: "home", label: "Home", Icon: IconHome },
  { id: "family", label: "Family", Icon: IconPeople },
  { id: "play", label: "Play", Icon: IconGame },
  { id: "news", label: "News", Icon: IconNews },
];

const MODES = [
  { id: "tree" as const, label: "Tree", Icon: IconTree },
  { id: "map" as const, label: "Map", Icon: IconMap },
  { id: "directory" as const, label: "Directory", Icon: IconGrid },
];

function headerTitle(tab: MainTab): string {
  switch (tab) {
    case "home":
      return "Sundius Family";
    case "family":
      return "Family";
    case "play":
      return "Play";
    case "news":
      return "Family News";
  }
}

export function AppShell() {
  const {
    activeTab,
    setActiveTab,
    familyBrowseMode,
    setFamilyBrowseMode,
    setShowSettings,
    setShowInvites,
    session,
    bundle,
    bundleLoading,
    bundleLoadError,
    continueIntoApp,
    replayOpening,
    replayHeritage,
    resetTabIntrosSeen,
    signOut,
    playInGame,
    setShowAddEvent,
    leaderboardByMode,
    me,
  } = useApp();
  const { shellMax } = useViewport();
  const [showIntro, setShowIntro] = useState<MainTab | null>(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showReplay, setShowReplay] = useState(false);

  const myRank = useMemo(() => {
    if (!me) return null;
    const overall = leaderboardByMode.overall ?? [];
    return overall.find((e) => e.person_id === me.person_id)?.rank ?? null;
  }, [leaderboardByMode, me]);

  const onTab = (tab: MainTab) => {
    setActiveTab(tab);
    setShowLeaderboard(false);
    if (!hasSeenTabIntro(tab)) {
      setShowIntro(tab);
      markTabIntroSeen(tab);
    }
  };

  if (bundleLoading && !bundle) {
    return (
      <div className="app-shell" style={{ maxWidth: shellMax }}>
        <LoadingState />
      </div>
    );
  }

  if (bundleLoadError && !bundle) {
    return (
      <div className="app-shell" style={{ maxWidth: shellMax }}>
        <ErrorState message={bundleLoadError} onRetry={() => void continueIntoApp()} />
      </div>
    );
  }

  if (!bundle || !session) return null;

  const hideChrome = playInGame;

  return (
    <div className="app-shell" style={{ maxWidth: shellMax }}>
      {showIntro ? (
        <TabIntroModal tab={showIntro} onContinue={() => setShowIntro(null)} />
      ) : null}

      {!hideChrome ? (
        <header className="header-bar">
          <h1
            className="header-title"
            onContextMenu={(e) => {
              e.preventDefault();
              setShowReplay(true);
            }}
          >
            {headerTitle(activeTab)}
          </h1>
          <div className="header-actions">
            {activeTab === "family" ? (
              <ShellActionButton label="Share" onClick={() => setShowInvites(true)}>
                <IconShare />
              </ShellActionButton>
            ) : null}
            {activeTab === "play" ? (
              <ShellActionButton
                label="Leaderboard"
                badge={myRank ? `#${myRank}` : null}
                onClick={() => setShowLeaderboard(true)}
              >
                <IconTrophy />
              </ShellActionButton>
            ) : null}
            {activeTab === "news" ? (
              <ShellActionButton label="Add event" onClick={() => setShowAddEvent(true)}>
                <IconPlus />
              </ShellActionButton>
            ) : null}
            {activeTab === "home" ? (
              <ShellActionButton label="Your settings" onClick={() => setShowSettings(true)}>
                <IconGear />
              </ShellActionButton>
            ) : null}
          </div>
        </header>
      ) : null}

      <main className={`screen ${hideChrome ? "screen-game" : ""}`}>
        {activeTab === "home" ? <HomeView /> : null}
        {activeTab === "family" ? (
          <div className="pad-screen stack">
            <div className="segmented">
              {MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  className={familyBrowseMode === mode.id ? "active" : ""}
                  onClick={() => setFamilyBrowseMode(mode.id)}
                >
                  <mode.Icon />
                  {mode.label}
                </button>
              ))}
            </div>
            {familyBrowseMode === "tree" ? <FamilyTreeView /> : null}
            {familyBrowseMode === "map" ? <FamilyMapView /> : null}
            {familyBrowseMode === "directory" ? <FamilyDirectoryView /> : null}
          </div>
        ) : null}
        {activeTab === "play" ? (
          <PlayView
            forceLeaderboard={showLeaderboard}
            onCloseLeaderboard={() => setShowLeaderboard(false)}
          />
        ) : null}
        {activeTab === "news" ? <NewsView /> : null}
      </main>

      {!hideChrome ? (
        <nav className="tab-bar" aria-label="Main">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`tab-btn ${activeTab === tab.id ? "active" : ""}`}
              aria-current={activeTab === tab.id ? "page" : undefined}
              onClick={() => onTab(tab.id)}
            >
              <tab.Icon />
              {tab.label}
            </button>
          ))}
        </nav>
      ) : null}

      <PersonDetailSheet />
      <SettingsSheet />
      <InvitesSheet />
      <FeedbackSheet />
      <FeedbackReplyModal />
      <EventComposerSheet />
      <AnnouncementComposerSheet />
      <BirthdayPrimingModal />
      <HeritageIntro />

      <ModalSheet
        open={showReplay}
        onClose={() => setShowReplay(false)}
        title="Replay"
      >
        <button type="button" className="btn-primary" onClick={() => { replayOpening(); setShowReplay(false); }}>
          Re-watch welcome & heritage
        </button>
        <button type="button" className="btn-ghost" onClick={() => { replayHeritage(); setShowReplay(false); }}>
          Re-watch heritage only
        </button>
        <button type="button" className="btn-ghost" onClick={() => { resetTabIntrosSeen(); setShowReplay(false); }}>
          Reset tab intros
        </button>
        <button type="button" className="btn-ghost" onClick={() => void signOut()}>
          Sign out
        </button>
      </ModalSheet>

      <UsageTracker />
    </div>
  );
}
